'use client';

import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react';
import Link from 'next/link';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'md' | 'sm';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'border border-transparent bg-accent text-white hover:bg-accent-hover',
  secondary:
    'border border-zinc-700 bg-zinc-900 text-zinc-100 hover:border-zinc-500 hover:bg-zinc-800 hover:text-zinc-50',
  ghost: 'border border-transparent text-zinc-400 hover:bg-white/5 hover:text-zinc-100',
  danger: 'border border-danger/40 bg-danger/[0.06] text-danger hover:border-danger hover:bg-danger/10',
};

// pointer-coarse: 44px targets on touch screens; desktop density is untouched.
const SIZE: Record<ButtonSize, string> = {
  md: 'h-8 gap-1.5 px-3 text-sm pointer-coarse:h-11',
  sm: 'h-7 gap-1.5 px-2.5 text-xs pointer-coarse:h-11',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon: Icon, loading = false, className, children, disabled, type, ...rest },
  ref
) {
  const iconClass = size === 'md' ? 'size-3.5' : 'size-3';
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={cn(
        'corner-brand-sm inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        className
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className={cn(iconClass, 'animate-spin')} aria-hidden />
      ) : (
        Icon && <Icon className={iconClass} aria-hidden />
      )}
      {children}
    </button>
  );
});

interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  disabled?: boolean;
  /**
   * Render a plain anchor instead of next/link, so the click leaves the app and
   * reloads the document. For the few places where the running bundle is the
   * thing that's broken (global-error after a chunk failure) — a soft nav there
   * just re-enters the same missing chunk.
   */
  hardNav?: boolean;
}

// A Button-styled real link, for navigation actions. Being an anchor keeps the
// browser's native affordances working: plain click navigates in-app (Next
// intercepts), while Cmd/Ctrl-click, middle-click and right-click → "Open in
// new tab" open a new tab.
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function LinkButton(
  { href, variant = 'secondary', size = 'md', icon: Icon, disabled = false, hardNav = false, className, children, ...rest },
  ref
) {
  const iconClass = size === 'md' ? 'size-3.5' : 'size-3';
  const shared = {
    ref,
    'aria-disabled': disabled || undefined,
    tabIndex: disabled ? -1 : undefined,
    className: cn(
      'corner-brand-sm inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap transition-colors',
      VARIANT[variant],
      SIZE[size],
      disabled && 'pointer-events-none cursor-not-allowed opacity-50',
      className
    ),
    ...rest,
  };
  const content = (
    <>
      {Icon && <Icon className={iconClass} aria-hidden />}
      {children}
    </>
  );
  return hardNav ? (
    <a href={href} {...shared}>
      {content}
    </a>
  ) : (
    <Link href={href} {...shared}>
      {content}
    </Link>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  /** Accessible name; also shown as the hover title. */
  label: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, size = 'md', variant = 'ghost', className, type, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      className={cn(
        'corner-brand-sm inline-flex shrink-0 items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        size === 'md' ? 'size-8 pointer-coarse:size-11' : 'size-7 pointer-coarse:size-11',
        className
      )}
      {...rest}
    >
      <Icon className={size === 'md' ? 'size-4' : 'size-3.5'} aria-hidden />
    </button>
  );
});
